import React, { useEffect, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Button,
  TextField,
} from "@mui/material";
import Inventory2OutlinedIcon from "@mui/icons-material/Inventory2Outlined";
import { colors, fonts } from "../../../theme/theme";

const CONFIRM_WORD = "archive";

/**
 * Typed confirmation for archiving a practitioner.
 *
 * The action is called "Archive", not "Delete", because nothing is erased: the
 * login is closed and the profile leaves the website, but the record stays so
 * payments, transfers and payout history keep their practitioner. Calling it
 * "Delete" would promise an erasure that does not happen.
 *
 * The typed word is the guard — a misclick in the row menu must not be able to
 * disable someone's account.
 */
const ArchivePractitionerDialog = ({ open, practitioner, onClose, onConfirm }) => {
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (open) {
      setText("");
      setBusy(false);
    }
  }, [open]);

  const matches = text.trim().toLowerCase() === CONFIRM_WORD;

  const close = () => {
    if (busy) return;
    onClose();
  };

  const confirm = async () => {
    if (!matches || busy) return;
    setBusy(true);
    const ok = await onConfirm(practitioner.id);
    setBusy(false);
    if (ok) onClose();
  };

  const name = practitioner?.name || practitioner?.email || "this practitioner";

  return (
    <Dialog open={open} onClose={close} maxWidth="sm" fullWidth>
      <DialogTitle
        sx={{
          fontFamily: fonts.gotham,
          color: colors.primary,
          display: "flex",
          alignItems: "center",
          gap: 1,
        }}
      >
        <Inventory2OutlinedIcon />
        Archive practitioner
      </DialogTitle>
      <DialogContent>
        <Box sx={{ fontFamily: fonts.quicksand, fontSize: 15, color: colors.text }}>
          You are about to archive <strong>{name}</strong>
          {practitioner?.email ? ` (${practitioner.email})` : ""}.
        </Box>

        <Box
          component="ul"
          sx={{
            fontFamily: fonts.quicksand,
            fontSize: 14,
            color: colors.text,
            mt: 2,
            mb: 0,
            pl: 2.5,
            "& li": { mb: 0.5 },
          }}
        >
          <li>Their login stops working immediately and open sessions are cut.</li>
          <li>Their profile is removed from the Nirva website directory.</li>
          <li>They leave this list and appear under the “Archived” filter.</li>
          <li>Any profile changes waiting for review are discarded.</li>
        </Box>

        <Box
          sx={{
            mt: 2,
            p: 1.5,
            borderRadius: 2,
            backgroundColor: "#F4F6F8",
            fontFamily: fonts.quicksand,
            fontSize: 13,
            color: colors.textLight,
          }}
        >
          Nothing is erased. Payments, transfers and payout history are kept and stay linked to this
          practitioner — archiving an account must not rewrite the books. An admin can restore the
          account at any time from the “Archived” filter.
        </Box>

        <Box sx={{ mt: 2.5, fontFamily: fonts.quicksand, fontSize: 14, color: colors.text }}>
          Type <strong>{CONFIRM_WORD}</strong> to confirm:
        </Box>
        <TextField
          fullWidth
          size="small"
          autoFocus
          value={text}
          onChange={(e) => setText(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && matches && confirm()}
          placeholder={CONFIRM_WORD}
          disabled={busy}
          sx={{ mt: 1, "& .MuiInputBase-input": { fontFamily: fonts.quicksand } }}
        />
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
        <Button
          variant="outlined"
          onClick={close}
          disabled={busy}
          sx={{
            textTransform: "none",
            fontFamily: fonts.quicksand,
            borderColor: colors.lightBorder,
            color: colors.text,
          }}
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={confirm}
          disabled={!matches || busy}
          sx={{
            textTransform: "none",
            fontFamily: fonts.quicksand,
            backgroundColor: "#C77A22",
            "&:hover": { backgroundColor: "#A9651A" },
          }}
        >
          {busy ? "Archiving..." : "Archive practitioner"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ArchivePractitionerDialog;
